import { html, internalProperty, property } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { nothing } from 'lit-html';
import { checkSlots } from '../../shared/slot-utils';
import {
  KatLitElement,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import changeoverAnchorStyles from './changeover-anchor.lit.scss';
import getString from './strings';

/**
 * @component {kat-changeover-anchor} KatalChangeover The changeover can be used when no page reload is necessary, but it is important to provide immediate feedback to the user about the action they've taken. They're an alternative to more traditional inline notifications such as a <a href="/components/statusindicator/">Status Indicator</a> or an <a href="/components/alert/">Alert</a>.
 * @guideline Do Use when you must provide immediate feedback to the user, but it doesn’t make sense, or you don't have enough room, to provide that feedback inline.
 * @guideline Do The feedback should be lightweight and simple in nature.
 * @guideline Do Connect the message in the changeover to the action that spawned it.
 * @guideline Dont Don't include interaction (buttons, hyperlinks, etc.,) or images in a changeover.
 * @guideline Dont Don't use for complex decision making or dwell experiences. They appear and disappear too quickly.
 * @guideline Dont Don't create a multi-step changeover. With the exception of a loading state, a changeover is, by definition, only one step.
 * @guideline Do If populating the CTA slot with an action, use a single link (kat-link). In the rare case where there are multiple potential actions (such as snoozing a changeover for one of several predetermined time periods) use a dropdown button, or text links within the content.
 * @status Production
 * @theme flo
 * @slot default Content that the changeover will display
 * @slot cta Contents will appear on the right of the text in the changeover. See guidelines for how this should be used.

 * @example Simple {
 * "align-x": "left",
 * "align-y": "center",
 * "content": "
 *   <p>Example content that the changeover will overlap!</p>
 *   <kat-button id=\"kat-changeover-anchor-trigger\" label=\"Trigger (show)\" variant=\"primary\"></kat-button>
 * ",
 * "script": "
 *   var anchor = document.querySelector(\"#preview kat-changeover-anchor\");
 *   var trigger = document.querySelector(\"#preview #kat-changeover-anchor-trigger\");
 *   trigger.addEventListener(\"click\", function () {
 *       anchor.show({
 *          icon: \"success\",
 *          label: \"You did it\",
 *       });
 *   });
 * "}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-changeover-anchor')
export class KatChangeoverAnchor extends KatLitElement {
  /**
   * Defines how the changeover is aligned horizontally inside the anchor.
   * @enum {value} left Align to the left side.
   * @enum {value} center Align to the center. This is the default.
   * @enum {value} right Align to the right side.
   */
  @property({ attribute: 'align-x' })
  alignX = 'center';

  /**
   * Defines how the changeover is aligned vertically inside the anchor.
   * @enum {value} top Align to the top side.
   * @enum {value} center Align to the center. This is the default.
   * @enum {value} bottom Align to the bottom side.
   */
  @property({ attribute: 'align-y' })
  alignY = 'center';

  /** Determines whether to show or hide the changeover's close icon */
  @property({ attribute: 'dismissable' })
  dismissable?: boolean;

  /**
   * Specifies how many milliseconds the changeover displays for until it is automatically hidden. This attribute is the
   * fallback value, the dismissDelay specified when calling the "show" method take precedence. Defaults to 5000.
   */
  @property({ attribute: 'dismiss-delay' })
  dismissDelay = 5000;

  /** Locale for the dismissable button label string */
  @property()
  locale?: string;

  private _childrenChanged() {
    this.requestUpdate();
  }

  @internalProperty()
  _changeoverVisible: boolean;

  @internalProperty()
  _config: any = {};

  @internalProperty()
  _autoDismissTimeout: any;

  /** Fires when changeover appears the first time. */
  @event('show', true)
  private _show: EventEmitter;

  /** Fires when changeover state changes while visible. */
  @event('change', true)
  private _change: EventEmitter;

  /** Fires when the changeover is hidden. */
  @event('hide', true)
  private _hide: EventEmitter;

  static get styles() {
    return [changeoverAnchorStyles];
  }

  firstUpdated() {
    this.observeChildren(this, () => this.requestUpdate());
  }

  /**
   * Contains the configuration of the changeover.
   * @typedef {Object} ChangeOverConfig
   * @property {string} icon The type of icon to display (shown on the left side).
   * @property {string} label The label to display (shown on the right side). Truncates with an ellipsis if larger than the container.
   * @property {string} ariaLabel The label read to screenreader users. Should be used to describe both the icon and context.
   * @property {boolean} sticky If true, then the changeover displays until it's changed or hidden. Takes precedence over dismissDelay. Defaults to false.
   * @property {number} dismissDelay Specifies how many milliseconds the changeover will display for until it is automatically hidden.
   */
  /**
   * Shows the changeover and changes it's state based on the config object passed.
   * @katalmethod
   * @param {ChangeOverConfig} config
   */
  show(config) {
    const wasVisible = this._changeoverVisible;
    this._changeoverVisible = true;
    this._config = config || {};
    this.dismissable = config.dismissable;

    if (config.dismissDelay) {
      this.dismissDelay = config.dismissDelay;
    }

    if (wasVisible) {
      this._change.emit();
    } else {
      this._show.emit();
    }

    if (!config.sticky) {
      this._autoDismissTimeout = setTimeout(() => {
        this.hide();
      }, this.dismissDelay);
    }
  }

  /**
   * Hides the changeover or does nothing if the changeover is already hidden.
   * @katalmethod
   */
  hide() {
    this._changeoverVisible = false;

    clearTimeout(this._autoDismissTimeout);
    this._hide.emit();
  }

  _alignToFlex(alignValue) {
    if (alignValue === 'left' || alignValue === 'top') {
      return 'flex-start';
    }
    if (alignValue === 'right' || alignValue === 'bottom') {
      return 'flex-end';
    }

    return 'center';
  }

  render() {
    const justify = this._alignToFlex(this.alignX);
    const align = this._alignToFlex(this.alignY);
    const classes = {
      changeover: true,
      'changeover-show': this._changeoverVisible,
    };
    // Only output the ariaLabel when the changeover is visible. This will cause the aria-live div to read the contents.
    const ariaLabel = this._changeoverVisible ? this._config.ariaLabel : '';

    const icon = this._config.icon
      ? html`
          <kat-icon
            class="changeover-icon"
            name=${this._config.icon}
            size="small"
          ></kat-icon>
        `
      : nothing;
    const label = this._config.label
      ? html`<span class="changeover-label">${this._config.label}</span>`
      : nothing;
    const slots = checkSlots(this);
    const ctaClasses = {
      cta: !!slots.cta,
    };

    return html`
      <slot></slot>
      <div
        class="container"
        style="justify-content: ${justify}; align-items: ${align};"
        aria-live="polite"
        aria-hidden="${!this._changeoverVisible}"
      >
        <div class=${classMap(classes)} part="changeover-content">
          ${icon} ${label}
          <div class="${classMap(ctaClasses)}">
            <slot name="cta" @slotchange=${this._childrenChanged}></slot>
          </div>
          ${
            this.dismissable
              ? html`
            <button
              class="close"
              type="button"
              @click=${this.hide}
              part="changeover-anchor-close-button"
              ?disabled=${!this._changeoverVisible}
              aria-label="${getString('close_button_label', null, this.locale)}"
            >
              <kat-icon name="close" size="small">
            </button>
          `
              : nothing
          }
        </div>
          <div class="kat-visually-hidden">${ariaLabel}</div>
        </div>
      </div>
    `;
  }
}
